/**
 * dashboardCache — sessionStorage-backed snapshot of the shell's shared data.
 *
 * Lets the dashboard shell paint last-known submissions / pending emails /
 * subscription instantly on a hard reload (stale-while-revalidate) instead of a
 * full spinner, then revalidate quietly in the background. Keyed with the
 * owning user's email so a different account in the same tab never sees the
 * previous user's snapshot (the shell also drops it on an email mismatch).
 *
 * Session-scoped on purpose: it clears when the tab session ends, so nothing
 * user-specific lingers on the machine longer than the browsing session.
 */

import type {
  DashboardSubmission,
  EmailProviderConnection,
  SubscriptionSettings,
} from './DashboardContext';
import type { UserPendingEmail } from './UserApprovalQueue';

export interface ShellSnapshot {
  email: string;
  submissions: DashboardSubmission[];
  pendingCount: number;
  pendingEmails: UserPendingEmail[];
  subscription: SubscriptionSettings | null;
  /**
   * Included so a reload can paint the mailbox state immediately. Without it
   * the shell had to hold isLoading until the provider fetch landed — that is
   * why the auto-send control appeared seconds after everything else, and why
   * the "connect a mailbox" banner flashed at users who were fully set up.
   */
  emailProvider: EmailProviderConnection | null;
}

/**
 * v2: snapshot gained emailProvider. The key change discards v1 blobs, which
 * would otherwise seed `undefined` and defeat the instant paint.
 *
 * Exported so tests seed and inspect storage by the same key the helpers use —
 * a hard-coded copy silently detached them the last time this was bumped.
 * Reads and writes still belong in the helpers below.
 */
export const SHELL_SNAPSHOT_KEY = 'sendvc_dashboard_shell_v2';

/** Read the persisted snapshot, or null if absent/unreadable/not in a browser. */
export function readShellSnapshot(): ShellSnapshot | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(SHELL_SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ShellSnapshot;
    if (!parsed || typeof parsed.email !== 'string' || !Array.isArray(parsed.submissions)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Persist a snapshot. The pending-email list can be large (bodies included), so
 * on a quota error we retry with an empty list — the badge/count stay instant
 * and the queue just revalidates from the network.
 */
export function writeShellSnapshot(snapshot: ShellSnapshot): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(SHELL_SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch {
    try {
      window.sessionStorage.setItem(SHELL_SNAPSHOT_KEY, JSON.stringify({ ...snapshot, pendingEmails: [] }));
    } catch {
      /* out of quota / disabled — non-fatal, we just skip persistence */
    }
  }
}

/** Drop the snapshot (e.g. a different user authenticated in this tab). */
export function clearShellSnapshot(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(SHELL_SNAPSHOT_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Read the snapshot only if it belongs to `email`, dropping it otherwise.
 *
 * The shell calls this once the session probe has named the authenticated
 * account, so a snapshot left behind by a previous user in this tab is never
 * seeded and never reaches the DOM. Seeding first and reconciling afterwards
 * put the other account's submissions and queued emails on screen for the
 * length of one probe.
 */
export function takeShellSnapshotFor(email: string): ShellSnapshot | null {
  const snapshot = readShellSnapshot();
  if (!snapshot) return null;
  if (snapshot.email !== email) {
    clearShellSnapshot();
    return null;
  }
  return snapshot;
}
